import { Button } from "@/components/ui/button";
import {
  InAppNotification,
  NotificationType,
  useMarkNotificationAsRead,
} from "@/lib/api/notifications";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { cn } from "@/lib/utils";
import {
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  StarIcon,
  UserIcon,
  VideoCameraIcon,
  CameraIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { BrandIcon } from "@somethings-ui/web";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { replaceTimePlaceholders } from "@/lib/utils/notification";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

const SHAREABLE_ENTITY_CONFIG: Record<
  string,
  {
    label: string;
    bgColor: string;
    iconColor: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  note: {
    label: "View Note",
    bgColor: "bg-orange-100",
    iconColor: "text-orange-brand",
    icon: DocumentTextIcon,
  },
  moment: {
    label: "View",
    bgColor: "bg-blue-100",
    iconColor: "text-blue-brand",
    icon: CameraIcon,
  },
  swap: {
    label: "View Swap",
    bgColor: "bg-purple-100",
    iconColor: "text-purple-600",
    icon: ArrowPathIcon,
  },
};

const getShareableEntityConfig = (entityType?: string) => {
  const key = entityType?.toLowerCase() ?? "";
  return SHAREABLE_ENTITY_CONFIG[key] ?? SHAREABLE_ENTITY_CONFIG.note;
};

const getShareableEntityLabel = (entityType?: string) => {
  return getShareableEntityConfig(entityType).label;
};

interface NotificationItemProps {
  notification: InAppNotification;
  timezone: string;
  className?: string;
  isAdmin?: boolean;
  setClose?: () => void;
}

const NotificationActions = ({
  notification,
  isAdmin,
  setClose,
}: {
  notification: InAppNotification;
  isAdmin?: boolean;
  setClose?: () => void;
}) => {
  const { mentorView, setMentorView } = useMentorView();
  const routerState = useRouterState();
  const router = useRouter();
  const { mutateAsync: markAsRead } = useMarkNotificationAsRead();
  const { setCurrentConversation } = useCurrentConversation();
  const { type, data } = notification;
  const { track } = useTrackEvent();

  const trackTodoClicked = () => {
    track(PostHogEvents.TODO_CLICKED, {
      notification_type: notification.type,
      notification_id: notification.id,
      teen_name: notification.data?.sender?.name,
    });
  };

  const markAsReadAction = {
    label: "Mark as Read",
    onClick: () => {
      trackTodoClicked();
      markAsRead(notification.id);
    },
  };

  const dismissAction = notification.read_at
    ? undefined // If the notification is already read, don't show the dismiss action
    : {
        label: "Dismiss",
        onClick: () => {
          trackTodoClicked();
          markAsRead(notification.id);
        },
      };

  let primaryAction:
    | {
        label: string;
        onClick: () => void;
        variant?: "dark" | "light";
      }
    | undefined;

  let secondaryAction:
    | {
        label: string;
        onClick: () => void;
      }
    | undefined;

  const handleNotificationClick = () => {
    trackTodoClicked();

    if (notification.type === NotificationType.MENTOR_MONTHLY_UPDATE) {
      router.navigate({
        to: `/mentor-monthly-updates/${notification.data?.mentorshipId}`,
      });
      setClose?.();
    } else {
      if (routerState.location.pathname !== "/") {
        router.navigate({
          to: "/conversations",
        });
      }
      if (!mentorView) {
        setMentorView(true);
      }
      if (notification.data?.sdkUrl) {
        setCurrentConversation(notification.data?.sdkUrl as string);
      }
    }

    // If the notification is not read, mark it as read
    if (
      !notification.read_at &&
      notification.type !== NotificationType.WHAT_TO_WORK_ON
    ) {
      markAsRead(notification.id);
    }
  };

  const handleViewShareableEntity = () => {
    const entityId = data?.entityId ?? data?.noteId;
    if (!entityId) {
      console.warn("No entityId found in notification data", data);
      return;
    }
    
    const entityType = (data?.sharedEntityType ?? "note").toLowerCase();
    
    if (routerState.location.pathname !== "/conversations") {
      router.navigate({ to: "/conversations" });
    }
    if (!mentorView) {
      setMentorView(true);
    }
    if (data?.sdkUrl) {
      setCurrentConversation(data.sdkUrl as string);
    }
    
    useCurrentConversation.getState().setPendingEntity({
      id: entityId,
      type: entityType.toUpperCase(),
    });
    
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  switch (type) {
    case NotificationType.WHAT_TO_WORK_ON:
      primaryAction = {
        label: "Go to conversation",
        onClick: handleNotificationClick,
      };
      secondaryAction = undefined;
      break;
    case NotificationType.NEW_TEEN:
      primaryAction = {
        label: "Meet Your New Teen",
        onClick: handleNotificationClick,
      };
      break;
    case NotificationType.ESCALATION:
      primaryAction = {
        label: "View Escalation Note",
        onClick: handleNotificationClick,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.NOTE_SHARED:
      primaryAction = {
        label: "View Note",
        onClick: handleNotificationClick,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.SESSION:
      primaryAction = markAsReadAction;
      break;
    case NotificationType.REMINDER:
      primaryAction = markAsReadAction;
      break;

    case NotificationType.TEEN_DEACTIVATED:
      primaryAction = markAsReadAction;
      break;
    case NotificationType.MENTOR_MONTHLY_UPDATE:
      primaryAction = {
        label: "Record Monthly Update",
        onClick: handleNotificationClick,
      };
      break;
    case NotificationType.SHAREABLE_ENTITY_SHARED: {
      const isSwapPending = data?.sharedEntityType?.toLowerCase() === "swap" && data?.pending_other_user;
      primaryAction = {
        label: isSwapPending ? "Add Your Photo" : getShareableEntityLabel(data?.sharedEntityType),
        onClick: handleViewShareableEntity,
        variant: "dark"
      };
      secondaryAction = dismissAction;
      break;
    }
    case NotificationType.SWAP_ACTION_REQUIRED: {
      primaryAction = {
        label: "Add Your Photo",
        onClick: handleViewShareableEntity,
        variant: "dark"
      };
      secondaryAction = dismissAction;
      break;
    }
    default:
      primaryAction = markAsReadAction;
      break;
  }

  if (isAdmin) {
    return null;
  }

  if (!primaryAction && !secondaryAction) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="flex items-center gap-2 pt-2">
        {primaryAction && (
          <Button
            onClick={primaryAction.onClick}
            variant="outline"
            className={cn("w-full", primaryAction.variant === "dark" ? "bg-black text-white" : "")}
          >
            {primaryAction.label}
          </Button>
        )}
        {secondaryAction && (
          <Button variant="ghost" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
};

const NotificationIcon = ({ type, entityType }: { type: NotificationType; entityType?: string }) => {
  switch (type) {
    case NotificationType.NEW_TEEN:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          icon={<UserIcon className="text-blue-brand" />}
          size="sm"
        />
      );
    case NotificationType.NOTE_SHARED:
      return (
        <BrandIcon
          bgColor="bg-orange-100"
          icon={<StarIcon className="text-orange-brand" />}
          size="sm"
        />
      );
    case NotificationType.SESSION:
      return (
        <BrandIcon
          bgColor="bg-pink-100"
          icon={<CalendarIcon className="text-pink-brand" />}
          size="sm"
        />
      );
    case NotificationType.TEEN_DEACTIVATED:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          icon={<FlagIcon className="text-error-brand" />}
          size="sm"
        />
      );
    case NotificationType.ESCALATION:
    case NotificationType.NEW_ESCALATION:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          size="sm"
          icon={<ExclamationTriangleIcon className="text-error-brand" />}
        />
      );
    case NotificationType.MENTOR_MONTHLY_UPDATE:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          size="sm"
          icon={<VideoCameraIcon className="text-blue-brand" />}
        />
      );
    case NotificationType.WHAT_TO_WORK_ON:
      return (
        <BrandIcon
          bgColor="bg-green-100"
          size="sm"
          icon={<CheckCircleIcon className="text-green-600" />}
        />
      );
    case NotificationType.SHAREABLE_ENTITY_SHARED: {
      const config = getShareableEntityConfig(entityType);
      const Icon = config.icon;
      return (
        <BrandIcon
          bgColor={config.bgColor}
          size="sm"
          icon={<Icon className={config.iconColor} />}
        />
      );
    }
    case NotificationType.SWAP_ACTION_REQUIRED: {
      const config = getShareableEntityConfig("swap");
      const Icon = config.icon;
      return (
        <BrandIcon
          bgColor={config.bgColor}
          size="sm"
          icon={<Icon className={config.iconColor} />}
        />
      );
    }
  }
};

export function NotificationItem({
  notification,
  timezone,
  className,
  isAdmin,
  setClose,
}: NotificationItemProps) {
  const { title, content, created_at, type, data } = notification;

  return (
    <div
      className={cn(
        "hover:bg-accent/5 border-b p-4 transition-colors",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-tertiary">
                {data?.sender?.name ?? data?.ownerName}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">
              {formatDistanceToNow(new Date(created_at), { addSuffix: true })}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <NotificationIcon type={type} entityType={data?.sharedEntityType} />
            <h4 className="text-sm font-bold">{title}</h4>
          </div>

          {/* Content */}
          <p className="text-muted-foreground text-sm">
            {replaceTimePlaceholders(content, { timezone })}
          </p>

          <NotificationActions
            notification={notification}
            isAdmin={isAdmin}
            setClose={setClose}
          />
        </div>
      </div>
    </div>
  );
}
